import React, { useState, useEffect, useCallback } from 'react';
import {
  LayoutDashboard,
  Users,
  Package,
  UserPlus,
  BarChart3,
  Wallet,
  Banknote,
  Layers,
  MapPin,
  Flame,
  Shield,
  ShieldCheck,
  Calculator,
  Gift,
  Megaphone,
  UserCog,
  LogOut,
  X
} from 'lucide-react';
import TlIcon from '../TlIcon';
import { ReactComponent as HizliyoLogo } from '../../assets/hizliyo-logo.svg';
import { apiService } from '../../services/api.service';
import { API_ENDPOINTS, FLEET_HUB_METHODS, FLEET_APP_EVENTS } from '../../config/environment';
import { visibleNavGroups } from '../../utils/fleetPermissions';
import './Sidebar.css';

/**
 * Menü, kullanıcının işine göre gruplanır: günü yönetmek, kuryeleri yönetmek, parayı takip etmek,
 * firmayı ayarlamak. Eski "Finans" grubu sekiz satırla en kalabalık gruptu ve tarife AYARINI
 * hakediş TAKİBİyle aynı listede karıştırıyordu; dört hakediş ekranı ayrı satırdaydı, kullanıcı
 * kaydını ekran değiştirerek arıyordu. Dördü tek "Hakedişler" girişinin sekmesi oldu.
 */
const NAV_GROUPS = [
  {
    label: 'Operasyon',
    items: [
      { id: 'dashboard', label: 'Anasayfa', icon: LayoutDashboard },
      { id: 'deliveries', label: 'Teslimatlar', icon: Package, badge: 'deliveries' },
      { id: 'live-map', label: 'Canlı Harita', icon: MapPin },
      { id: 'heatmap', label: 'Isı Haritası', icon: Flame },
    ]
  },
  {
    label: 'Kuryeler',
    items: [
      { id: 'members', label: 'Kuryelerim', icon: Users },
      { id: 'join-requests', label: 'Katılma Talepleri', icon: UserPlus, badge: true },
      { id: 'courier-costs', label: 'Kurye Ücretleri', icon: Calculator },
      { id: 'package-bonus', label: 'Paket Bonusu', icon: Gift },
    ]
  },
  {
    label: 'Kazanç',
    items: [
      { id: 'payouts', label: 'Hakedişler', icon: Banknote },
      { id: 'earnings', label: 'Gelir Paylaşımı', icon: Wallet },
      { id: 'cost-history', label: 'Filo Tarifesi', icon: TlIcon },
      { id: 'stacking-fee', label: 'Yığın Kuralları', icon: Layers },
      { id: 'reports', label: 'Raporlar', icon: BarChart3 },
    ]
  },
  {
    label: 'Firma',
    items: [
      { id: 'fleet-readiness', label: 'Hazırlık Durumu', icon: ShieldCheck },
      { id: 'supervisors', label: 'Bölge Sorumluları', icon: UserCog },
      { id: 'legal-agreements', label: 'Sözleşmelerim', icon: Shield },
      { id: 'announcements', label: 'Duyurular', icon: Megaphone },
    ]
  },
];

/** Eski girişler Hakedişler sekmelerine taşındı — eski bağlantıda da doğru satır vurgulanır. */
const PAYOUT_ALIASES = ['fleet-payments', 'workday-earnings', 'courier-payments', 'invoices'];

const Sidebar = ({ activePage, onNavigate, onLogout, isOpen, onToggle, openDeliveryCount = 0, criticalCount = 0, fleetName, fleetLogo, accessCtx }) => {
  const [pendingJoinCount, setPendingJoinCount] = useState(0);

  // accessCtx henüz gelmediyse menü çizilmez; yanlış menüyü bir an gösterip geri almak,
  // sorumluya erişemeyeceği sayfaları göstermek demektir. Sahipte tüm gruplar aynen kalır.
  const groups = accessCtx ? visibleNavGroups(NAV_GROUPS, accessCtx) : [];

  const fetchPendingCount = useCallback(async () => {
    try {
      const response = await apiService.post(API_ENDPOINTS.FLEET_PANEL.JOIN_REQUESTS_LIST, {
        page: 1,
        pageSize: 1,
        filterModel: { Status: { filter: '1', type: 'eq' } }
      });
      if (response.success) {
        setPendingJoinCount(response.data?.totalCount || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    fetchPendingCount();
  }, [fetchPendingCount]);

  /* Rozet iki kaynakla tazelenir: (1) karşı taraftan gelen SignalR bildirimi (yeni talep),
     (2) bu panelde yapılan onay/red (JOIN_REQUESTS_CHANGED). İkincisi olmadan kullanıcı talebi
     onayladıktan sonra rozet eski sayıda kalıyordu — kendi işlemi push üretmiyor. */
  useEffect(() => {
    const handler = () => fetchPendingCount();
    const signalrEvent = `fleet:${FLEET_HUB_METHODS.RECEIVE_JOIN_REQUEST_NOTIFICATION}`;
    window.addEventListener(signalrEvent, handler);
    window.addEventListener(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED, handler);
    return () => {
      window.removeEventListener(signalrEvent, handler);
      window.removeEventListener(FLEET_APP_EVENTS.JOIN_REQUESTS_CHANGED, handler);
    };
  }, [fetchPendingCount]);

  const currentPage = PAYOUT_ALIASES.includes(activePage) ? 'payouts' : activePage;

  return (
    <div className={`fleet-sidebar ${isOpen ? 'open' : 'closed'}`}>
      <div className="sidebar-header">
        {/* Filo kendi logosunu yüklediyse marka alanı onun olur; yoksa Hızlıyo görseli kalır. */}
        <div className="brand-container">
          {fleetLogo ? (
            <div className="brand-fleet-logo" title={fleetName || 'Filo'}>
              <img src={fleetLogo} alt={fleetName || 'Firma logosu'} />
            </div>
          ) : (
            <HizliyoLogo className="brand-logo" />
          )}
          <span className="brand-badge">Filo</span>
        </div>
        <button className="sidebar-close-btn" onClick={onToggle}>
          <X size={18} />
        </button>
      </div>

      <nav className="sidebar-nav">
        {groups.map((group) => (
          <div key={group.label} className="nav-group">
            <div className="nav-group-label">{group.label}</div>
            {group.items.map((item) => {
              const Icon = item.icon;
              const isActive = currentPage === item.id;

              /* Teslimatlar rozeti HER ZAMAN açık paket sayısını gösterir; kritik paket
                 yalnız RENGİ değiştirir (kırmızı+pulse). Önceden kritik varken sayı kritik
                 sayısına dönüyordu: 12 açık paketin 2'si kritikken rozet "2" yazıyor,
                 kullanıcı iş yükünün azaldığını sanıyordu. Kritik sayısı ipucunda duruyor. */
              const isDeliveries = item.badge === 'deliveries';
              const deliveriesCritical = isDeliveries && criticalCount > 0;
              const count = item.badge === true ? pendingJoinCount
                  : isDeliveries ? openDeliveryCount : 0;
              const badgeClass = deliveriesCritical ? 'nav-badge nav-badge-critical'
                  : isDeliveries ? 'nav-badge nav-badge-orange' : 'nav-badge';
              const badgeTitle = !isDeliveries ? undefined
                  : deliveriesCritical
                      ? `${openDeliveryCount} açık paket, ${criticalCount} kritik`
                      : `${openDeliveryCount} açık paket`;
              return (
                <button
                  key={item.id}
                  className={`nav-item ${isActive ? 'active' : ''}`}
                  onClick={() => onNavigate(item.id)}
                  title={badgeTitle}
                >
                  <Icon size={18} className="nav-icon" />
                  <span className="nav-label">{item.label}</span>
                  {count > 0 && <span className={badgeClass}>{count > 99 ? '99+' : count}</span>}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      <div className="sidebar-footer">
        <button className="logout-btn" onClick={onLogout}>
          <LogOut size={18} />
          <span>Çıkış Yap</span>
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
